import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardActionArea, Stack, Typography, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import {
  Home as HomeIcon, Tune as SettingsIcon, LibraryBooks as SubjectIcon,
  Upload as ImportIcon, Notes as RemarksIcon,
  Assessment as StudentReportIcon, Groups as GroupReportIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import API from '../../BaseURL';

// Quick-launch tile shown on the Examination Home grid.
function Tile({ icon, label, desc, to, color = 'primary' }) {
  const nav = useNavigate();
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={() => nav(to)} sx={{ p: 2.5, height: '100%' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.8 }}>
          <Box sx={{ color: `${color}.main`, display: 'flex' }}>{icon}</Box>
          <Typography fontWeight={800}>{label}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35 }}>
          {desc}
        </Typography>
      </CardActionArea>
    </Card>
  );
}

export default function ExaminationHomePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let on = true;
    API.get('/examination/home')
      .then(r => { if (on) setData(r.data); })
      .catch(e => { if (on) setError(e.response?.data?.message || 'Could not load examination home'); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  const period = data?.period;
  const counts = data?.counts || {};

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <ModuleHeader
        icon={<HomeIcon />}
        title="Examination"
        subtitle="Register subjects, capture scores, generate report cards and manage promotions"
      />

      {/* Active period banner */}
      <Card sx={{ mb: 2, p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}
          justifyContent="space-between">
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Typography variant="overline" sx={{ letterSpacing: 1, fontWeight: 800, color: 'text.secondary' }}>
              Active period
            </Typography>
            {period ? (
              <>
                <Chip label={period.term || '—'} color="primary" sx={{ fontWeight: 800 }} />
                <Chip label={period.acc || '—'} variant="outlined" sx={{ fontWeight: 800 }} />
              </>
            ) : (
              <Chip label="No active period set" color="warning" />
            )}
          </Stack>
          {loading && <CircularProgress size={20} />}
          {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
        </Stack>
      </Card>

      {/* Counters */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Classes',          value: counts.classes },
          { label: 'Subjects',         value: counts.subjects },
          { label: 'Grade bands',      value: counts.grade_bands },
          { label: 'SBA rows',         value: counts.sba_rows },
          { label: 'Mock score rows',  value: counts.mock_rows },
          { label: 'Mid-Term rows',    value: counts.midterm_rows },
        ].map(c => (
          <Grid item xs={6} sm={4} md={2} key={c.label}>
            <Card variant="outlined" sx={{ p: 1.6, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary"
                sx={{ display: 'block', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10 }}>
                {c.label}
              </Typography>
              <Typography variant="h5" fontWeight={900} sx={{ mt: 0.4 }}>
                {Number(c.value || 0).toLocaleString()}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick launch */}
      <Typography variant="overline" sx={{ letterSpacing: 1, fontWeight: 800, color: 'text.secondary' }}>
        Quick launch
      </Typography>
      <Grid container spacing={1.5} sx={{ mt: 0.2, mb: 2 }}>
        <Grid item xs={12} sm={6} md={4} lg={3}><Tile icon={<SettingsIcon />}        label="System Settings"        desc="Grading scale and class/exam score weights" to="/examination/settings" /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><Tile icon={<SubjectIcon />}         label="Register Subject"       desc="Subjects per class, term and exam type"     to="/examination/subjects" /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><Tile icon={<ImportIcon />}          label="Import Score Sheets"    desc="Mock, Mid-Term and Terminal — bulk load and compute" to="/examination/import" /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><Tile icon={<RemarksIcon />}         label="Import Remarks"         desc="Bulk-load conduct, attitude and remarks"    to="/examination/remarks" /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><Tile icon={<StudentReportIcon />}   label="Student Report"         desc="Single student report card"                  to="/examination/report/student" /></Grid>
        <Grid item xs={12} sm={6} md={4} lg={3}><Tile icon={<GroupReportIcon />}     label="Group Reporting"        desc="Batch report card generation for a class"    to="/examination/report/group" /></Grid>
      </Grid>

      {/* Subjects registered, grouped */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography fontWeight={800}>Subjects already registered</Typography>
          <Typography variant="caption" color="text.secondary">
            From <code>ex_re</code> — grouped by class, term, exam type and academic year.
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11 } }}>
                <TableCell>Class</TableCell>
                <TableCell>Term</TableCell>
                <TableCell>Academic Year</TableCell>
                <TableCell>Exam Type</TableCell>
                <TableCell align="right">Subjects</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><CircularProgress size={20} /></TableCell></TableRow>}
              {!loading && (data?.subjects_by_class || []).length === 0 && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No subjects registered yet — open <b>Register Subject</b> to add some.
                </TableCell></TableRow>
              )}
              {!loading && (data?.subjects_by_class || []).map((r, i) => (
                <TableRow key={i} hover sx={{ '& td': { fontSize: 12.5 } }}>
                  <TableCell sx={{ fontWeight: 700 }}>{r.cla || '—'}</TableCell>
                  <TableCell>{r.term || '—'}</TableCell>
                  <TableCell>{r.acc_yer || '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.state || '—'} variant="outlined" sx={{ fontWeight: 700, height: 20 }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{r.n}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
